import type {
  StreamAnchor,
  StreamCommentViewState,
  StudyGroupTabViewState,
  StudyGroupViewKey,
  StudyGroupViewState,
} from "./types";
import { deriveStudyGroupViewKey } from "./viewKey";

const STORAGE_VERSION = 1;
const STORAGE_PREFIX = `study-group-view-state:v${STORAGE_VERSION}:user:`;
const VIEW_RETENTION_MS = 30 * 60 * 1000;
const MAX_GROUPS = 5;
const MAX_VIEWS_PER_GROUP = 20;

type StorageLike = Pick<Storage, "getItem" | "setItem" | "removeItem">;

export type SessionStoreEviction = {
  userId: number | string;
  studyGroupId: number;
  viewKey: StudyGroupViewKey;
};

type SessionStoreEvictionHandler = (
  evictions: readonly SessionStoreEviction[],
) => void;

let defaultEvictionHandler: SessionStoreEvictionHandler | undefined;

export const setDefaultSessionStoreEvictionHandler = (
  handler: SessionStoreEvictionHandler,
): void => {
  defaultEvictionHandler = handler;
};

export type SessionStoreOptions = {
  storage?: StorageLike;
  now?: () => number;
};

export type TabViewStateInput = Omit<StudyGroupTabViewState, "savedAt"> & {
  savedAt?: number;
};

type StoredGroupState = {
  lastAccessedAt: number;
  state: StudyGroupViewState;
};

type StoredUserState = {
  version: typeof STORAGE_VERSION;
  groups: Record<string, StoredGroupState>;
};

type LoadedUserState = {
  state: StoredUserState;
  needsRepair: boolean;
};

const cacheByStorage = new WeakMap<object, Map<string, StoredUserState>>();

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === "object" && value !== null && !Array.isArray(value);

const isFiniteNumber = (value: unknown): value is number =>
  typeof value === "number" && Number.isFinite(value);

const isSafeInteger = (value: unknown): value is number =>
  isFiniteNumber(value) && Number.isSafeInteger(value);

const isPositiveInteger = (value: unknown): value is number =>
  isSafeInteger(value) && value > 0;

const clone = <T>(value: T): T => structuredClone(value);

const storageKeyFor = (userId: number | string): string =>
  `${STORAGE_PREFIX}${encodeURIComponent(String(userId))}`;

const getDefaultStorage = (): StorageLike | undefined => {
  try {
    return typeof window === "undefined" ? undefined : window.sessionStorage;
  } catch {
    return undefined;
  }
};

const resolveOptions = (options?: SessionStoreOptions) => ({
  storage: options?.storage ?? getDefaultStorage(),
  now: options?.now ?? Date.now,
});

const getCache = (storage: StorageLike): Map<string, StoredUserState> => {
  const storageObject = storage as object;
  const existing = cacheByStorage.get(storageObject);
  if (existing) return existing;

  const created = new Map<string, StoredUserState>();
  cacheByStorage.set(storageObject, created);
  return created;
};

const isViewKey = (value: unknown): value is StudyGroupViewKey =>
  typeof value === "string" &&
  (value === "stream:home" ||
    value === "events" ||
    /^stream:channel:[1-9]\d*$/.test(value) ||
    /^files:folder:(root|[1-9]\d*)$/.test(value) ||
    /^links:category:(root|[1-9]\d*)$/.test(value));

const normalizeAnchor = (value: unknown): StreamAnchor | undefined => {
  if (!isRecord(value)) return undefined;
  if (
    value.type !== "stream-post" ||
    !isPositiveInteger(value.contentId) ||
    !isFiniteNumber(value.offsetFromContainerTop)
  ) {
    return undefined;
  }

  return {
    type: "stream-post",
    contentId: value.contentId,
    offsetFromContainerTop: value.offsetFromContainerTop,
  };
};

const normalizeComments = (
  value: unknown,
): StreamCommentViewState | undefined => {
  if (!isRecord(value) || !Array.isArray(value.expandedPostContentIds)) {
    return undefined;
  }
  if (!isRecord(value.expandedReplyIdsByPost)) return undefined;

  const expandedPostContentIds =
    value.expandedPostContentIds.filter(isPositiveInteger);
  const expandedReplyIdsByPost: Record<number, number[]> = {};
  for (const [postId, replyIds] of Object.entries(
    value.expandedReplyIdsByPost,
  )) {
    if (!isPositiveInteger(Number(postId)) || !Array.isArray(replyIds))
      continue;
    const normalizedReplies = replyIds.filter(isPositiveInteger);
    if (normalizedReplies.length > 0)
      expandedReplyIdsByPost[Number(postId)] = normalizedReplies;
  }

  return { expandedPostContentIds, expandedReplyIdsByPost };
};

const normalizeTabViewState = (
  value: unknown,
  groupId: number,
  viewKey: StudyGroupViewKey,
  now: number,
): StudyGroupTabViewState | undefined => {
  if (!isRecord(value) || typeof value.pathname !== "string") return undefined;
  if (deriveStudyGroupViewKey(value.pathname, groupId) !== viewKey)
    return undefined;
  if (
    !isFiniteNumber(value.savedAt) ||
    value.savedAt > now ||
    now - value.savedAt >= VIEW_RETENTION_MS
  ) {
    return undefined;
  }

  const normalized: StudyGroupTabViewState = {
    pathname: value.pathname,
    savedAt: value.savedAt,
  };
  if (typeof value.retainedDatasetId === "string" && value.retainedDatasetId) {
    normalized.retainedDatasetId = value.retainedDatasetId;
  }
  if (isRecord(value.scroll) && typeof value.scroll.containerKey === "string") {
    if (isFiniteNumber(value.scroll.scrollTop) && value.scroll.scrollTop >= 0) {
      normalized.scroll = {
        containerKey: value.scroll.containerKey,
        scrollTop: value.scroll.scrollTop,
      };
      const anchor = normalizeAnchor(value.scroll.anchor);
      if (anchor) normalized.scroll.anchor = anchor;
    }
  }
  if (isRecord(value.stream)) {
    const comments = normalizeComments(value.stream.comments);
    const snapshot = value.stream.updateSnapshot;
    if (
      comments &&
      isRecord(snapshot) &&
      (snapshot.version === null || typeof snapshot.version === "string") &&
      typeof snapshot.snapshotAt === "string" &&
      isSafeInteger(snapshot.snapshotSequence)
    ) {
      normalized.stream = {
        comments,
        updateSnapshot: {
          version: snapshot.version,
          snapshotAt: snapshot.snapshotAt,
          snapshotSequence: snapshot.snapshotSequence,
        },
      };
    }
  }

  return normalized;
};

const newestViewKey = (
  views: Partial<Record<StudyGroupViewKey, StudyGroupTabViewState>>,
): StudyGroupViewKey | undefined => {
  let newest: StudyGroupViewKey | undefined;
  for (const key of Object.keys(views) as StudyGroupViewKey[]) {
    const candidate = views[key];
    if (!candidate) continue;
    const current = newest ? views[newest] : undefined;
    if (!current || candidate.savedAt > current.savedAt) newest = key;
  }
  return newest;
};

const limitViews = (state: StudyGroupViewState): StudyGroupViewKey[] => {
  const evicted: StudyGroupViewKey[] = [];
  while (Object.keys(state.views).length > MAX_VIEWS_PER_GROUP) {
    let oldest: StudyGroupViewKey | undefined;
    for (const key of Object.keys(state.views) as StudyGroupViewKey[]) {
      if (key === state.lastViewKey) continue;
      const candidate = state.views[key];
      const current = oldest ? state.views[oldest] : undefined;
      if (!candidate) continue;
      if (!current || candidate.savedAt < current.savedAt) {
        oldest = key;
      }
    }
    if (!oldest) break;
    delete state.views[oldest];
    evicted.push(oldest);
  }
  return evicted;
};

const normalizeGroup = (
  value: unknown,
  groupId: number,
  now: number,
): StoredGroupState | undefined => {
  if (
    !isRecord(value) ||
    !isRecord(value.state) ||
    !isRecord(value.state.views)
  ) {
    return undefined;
  }

  const views: Partial<Record<StudyGroupViewKey, StudyGroupTabViewState>> = {};
  for (const [key, tab] of Object.entries(value.state.views)) {
    if (!isViewKey(key)) continue;
    const normalizedTab = normalizeTabViewState(tab, groupId, key, now);
    if (normalizedTab) views[key] = normalizedTab;
  }

  const suppliedLastViewKey = value.state.lastViewKey;
  const lastViewKey =
    isViewKey(suppliedLastViewKey) && views[suppliedLastViewKey]
      ? suppliedLastViewKey
      : newestViewKey(views);
  if (!lastViewKey) return undefined;
  const lastView = views[lastViewKey];
  if (!lastView) return undefined;

  const state: StudyGroupViewState = {
    lastPathname: lastView.pathname,
    lastViewKey,
    views,
  };
  limitViews(state);
  return {
    lastAccessedAt:
      isFiniteNumber(value.lastAccessedAt) && value.lastAccessedAt <= now
        ? value.lastAccessedAt
        : lastView.savedAt,
    state,
  };
};

const limitGroups = (
  state: StoredUserState,
): { groupId: number; group: StoredGroupState }[] => {
  const evicted: { groupId: number; group: StoredGroupState }[] = [];
  while (Object.keys(state.groups).length > MAX_GROUPS) {
    let oldestGroupId: string | undefined;
    for (const groupId of Object.keys(state.groups)) {
      const candidate = state.groups[groupId];
      const current = oldestGroupId ? state.groups[oldestGroupId] : undefined;
      if (!candidate) continue;
      if (!current || candidate.lastAccessedAt < current.lastAccessedAt) {
        oldestGroupId = groupId;
      }
    }
    if (!oldestGroupId) return evicted;
    const oldestGroup = state.groups[oldestGroupId];
    if (oldestGroup) {
      evicted.push({ groupId: Number(oldestGroupId), group: oldestGroup });
    }
    delete state.groups[oldestGroupId];
  }
  return evicted;
};

const normalizeUserState = (value: unknown, now: number): StoredUserState => {
  const result: StoredUserState = { version: STORAGE_VERSION, groups: {} };
  if (
    !isRecord(value) ||
    value.version !== STORAGE_VERSION ||
    !isRecord(value.groups)
  ) {
    return result;
  }

  for (const [groupId, group] of Object.entries(value.groups)) {
    const parsedGroupId = Number(groupId);
    if (
      !isPositiveInteger(parsedGroupId) ||
      String(parsedGroupId) !== groupId
    ) {
      continue;
    }
    const normalized = normalizeGroup(group, parsedGroupId, now);
    if (normalized) result.groups[groupId] = normalized;
  }
  limitGroups(result);
  return result;
};

const loadUserState = (
  storage: StorageLike,
  key: string,
  now: number,
): LoadedUserState => {
  const cache = getCache(storage);
  const cached = cache.get(key);
  if (cached) {
    const state = normalizeUserState(cached, now);
    return {
      state,
      needsRepair: JSON.stringify(state) !== JSON.stringify(cached),
    };
  }

  try {
    const raw = storage.getItem(key);
    const parsed: unknown = raw ? JSON.parse(raw) : undefined;
    const state = normalizeUserState(parsed, now);
    return {
      state,
      needsRepair:
        raw !== null && JSON.stringify(state) !== JSON.stringify(parsed),
    };
  } catch {
    return {
      state: { version: STORAGE_VERSION, groups: {} },
      needsRepair: true,
    };
  }
};

const persist = (
  storage: StorageLike,
  key: string,
  state: StoredUserState,
): boolean => {
  try {
    storage.setItem(key, JSON.stringify(state));
    getCache(storage).set(key, clone(state));
    return true;
  } catch {
    return false;
  }
};

export const readGroupViewState = (
  userId: number | string,
  groupId: number,
  options?: SessionStoreOptions,
): StudyGroupViewState | null => {
  if (!isPositiveInteger(groupId)) return null;
  const { storage, now } = resolveOptions(options);
  if (!storage) return null;

  const key = storageKeyFor(userId);
  const accessedAt = now();
  const loaded = loadUserState(storage, key, accessedAt);
  const groupKey = String(groupId);
  const group = loaded.state.groups[groupKey];
  if (!group) {
    if (loaded.needsRepair) persist(storage, key, loaded.state);
    return null;
  }

  const next = clone(loaded.state);
  const nextGroup = next.groups[groupKey];
  if (!nextGroup) return clone(group.state);
  nextGroup.lastAccessedAt = accessedAt;
  if (loaded.needsRepair || group.lastAccessedAt !== accessedAt) {
    if (!persist(storage, key, next)) return clone(group.state);
  }
  return clone(nextGroup.state);
};

export const upsertTabViewState = (
  userId: number | string,
  groupId: number,
  viewKey: StudyGroupViewKey,
  input: TabViewStateInput,
  options?: SessionStoreOptions,
): boolean => {
  if (!isPositiveInteger(groupId) || !isViewKey(viewKey)) return false;
  const { storage, now } = resolveOptions(options);
  if (!storage) return false;

  const currentTime = now();
  const savedAt = input.savedAt ?? currentTime;
  const tab = normalizeTabViewState(
    { ...input, savedAt },
    groupId,
    viewKey,
    currentTime,
  );
  if (!tab) return false;

  const key = storageKeyFor(userId);
  const next = loadUserState(storage, key, currentTime).state;
  const previous = next.groups[String(groupId)]?.state;
  const views = { ...(previous?.views ?? {}), [viewKey]: tab };
  const groupState: StudyGroupViewState = {
    lastPathname: tab.pathname,
    lastViewKey: viewKey,
    views,
  };
  const evictions: SessionStoreEviction[] = limitViews(groupState).map(
    (evictedViewKey) => ({
      userId,
      studyGroupId: groupId,
      viewKey: evictedViewKey,
    }),
  );
  next.groups[String(groupId)] = {
    lastAccessedAt: currentTime,
    state: groupState,
  };
  const evictedGroups = limitGroups(next);
  for (const { groupId: evictedGroupId, group } of evictedGroups) {
    for (const evictedViewKey of Object.keys(
      group.state.views,
    ) as StudyGroupViewKey[]) {
      evictions.push({
        userId,
        studyGroupId: evictedGroupId,
        viewKey: evictedViewKey,
      });
    }
  }

  const persisted = persist(storage, key, next);
  if (persisted && evictions.length > 0) defaultEvictionHandler?.(evictions);
  return persisted;
};

export const downgradeViewToRouteOnly = (
  userId: number | string,
  groupId: number,
  viewKey: StudyGroupViewKey,
  options?: SessionStoreOptions,
): boolean => {
  if (!isPositiveInteger(groupId) || !isViewKey(viewKey)) return false;
  const { storage, now } = resolveOptions(options);
  if (!storage) return false;

  const key = storageKeyFor(userId);
  const next = loadUserState(storage, key, now()).state;
  const group = next.groups[String(groupId)];
  const current = group?.state.views[viewKey];
  if (!group || !current) return false;

  group.state.views[viewKey] = {
    pathname: current.pathname,
    savedAt: current.savedAt,
  };
  return persist(storage, key, next);
};

export const clearUserViewStates = (
  userId: number | string,
  options?: SessionStoreOptions,
): boolean => {
  const { storage } = resolveOptions(options);
  if (!storage) return false;

  const key = storageKeyFor(userId);
  try {
    storage.removeItem(key);
    getCache(storage).delete(key);
    return true;
  } catch {
    return false;
  }
};

export const clearAllUserViewStates = (
  options?: SessionStoreOptions,
): boolean => {
  const { storage } = resolveOptions(options);
  if (!storage) return false;

  const keys = new Set(
    [...getCache(storage).keys()].filter((key) =>
      key.startsWith(STORAGE_PREFIX),
    ),
  );
  const indexedStorage = storage as StorageLike &
    Partial<Pick<Storage, "key" | "length">>;
  if (
    typeof indexedStorage.key === "function" &&
    typeof indexedStorage.length === "number"
  ) {
    for (let index = 0; index < indexedStorage.length; index += 1) {
      const key = indexedStorage.key(index);
      if (key?.startsWith(STORAGE_PREFIX)) keys.add(key);
    }
  }

  try {
    for (const key of keys) storage.removeItem(key);
    const cache = getCache(storage);
    for (const key of keys) cache.delete(key);
    return true;
  } catch {
    return false;
  }
};
